// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import Channels from '@app/features/channel/state/Channels';
import * as ScheduledEventCommands from '@app/features/guild/commands/ScheduledEventCommands';
import styles from '@app/features/guild/components/modals/GuildEventsModal.module.css';
import {formatEventRange} from '@app/features/guild/utils/ScheduledEventUtils';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import {extractErrorMessage, pushApiErrorModal} from '@app/lib/forms';
import {ScheduledEventEntityType, ScheduledEventStatus} from '@fluxer/constants/src/ScheduledEventConstants';
import type {ScheduledEventResponse} from '@fluxer/schema/src/domains/guild/ScheduledEventSchemas';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {CheckIcon, MapPinIcon, SpeakerHighIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useState} from 'react';

const INTERESTED_DESCRIPTOR = msg({
	message: 'Interested',
	context: 'guild-events',
	comment: 'Button on an event card that marks the member as interested. Shows a check mark once selected.',
});

const INTERESTED_COUNT_DESCRIPTOR = msg({
	message: 'Interested: {count}',
	context: 'guild-events',
	comment: 'Label on an event card showing how many members are interested. {count} is a number.',
});

const GuildEventsModal: React.FC<{guildId: string}> = observer(({guildId}) => {
	const {i18n} = useLingui();
	const [events, setEvents] = useState<Array<ScheduledEventResponse> | null>(null);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		try {
			setEvents(await ScheduledEventCommands.fetchScheduledEvents(guildId));
		} catch (loadError) {
			setError(extractErrorMessage(i18n, loadError));
			setEvents([]);
		}
	}, [guildId, i18n]);

	useEffect(() => {
		void load();
	}, [load]);

	const toggleInterest = async (event: ScheduledEventResponse) => {
		try {
			const updated = await ScheduledEventCommands.setScheduledEventInterest(guildId, event.id, !event.user_interested);
			setEvents((current) => current?.map((entry) => (entry.id === updated.id ? updated : entry)) ?? null);
		} catch (toggleError) {
			pushApiErrorModal(i18n, toggleError);
		}
	};

	const openChannel = (channelId: string) => {
		ModalCommands.pop();
		NavigationCommands.selectChannel(guildId, channelId);
	};

	return (
		<Modal.Root size="small" centered data-flx="guild.guild-events-modal.modal-root">
			<Modal.Header title={<Trans>Events</Trans>} data-flx="guild.guild-events-modal.modal-header" />
			<Modal.Content data-flx="guild.guild-events-modal.modal-content">
				{events === null ? (
					<Spinner data-flx="guild.guild-events-modal.spinner" />
				) : (
					<div className={styles.list} data-flx="guild.guild-events-modal.list">
						{error && (
							<p className={styles.empty} data-flx="guild.guild-events-modal.empty">
								{error}
							</p>
						)}
						{!error && events.length === 0 && (
							<p className={styles.empty} data-flx="guild.guild-events-modal.empty--2">
								<Trans>No upcoming events.</Trans>
							</p>
						)}
						{events.map((event) => {
							const channelName =
								event.channel_id !== null ? (Channels.getChannel(event.channel_id)?.name ?? event.channel_id) : null;
							const isLive = event.status === ScheduledEventStatus.ACTIVE;
							return (
								<div key={event.id} className={styles.card} data-flx="guild.guild-events-modal.card">
									<div className={styles.cardHeader} data-flx="guild.guild-events-modal.card-header">
										<span className={styles.name} data-flx="guild.guild-events-modal.name">
											{event.name}
										</span>
										{isLive && (
											<span className={styles.live} data-flx="guild.guild-events-modal.live">
												<Trans>Live</Trans>
											</span>
										)}
									</div>
									<div className={styles.meta} data-flx="guild.guild-events-modal.meta">
										<span data-flx="guild.guild-events-modal.span">
											{formatEventRange(i18n.locale, event.scheduled_start, event.scheduled_end)}
										</span>
										{event.entity_type === ScheduledEventEntityType.EXTERNAL ? (
											<span data-flx="guild.guild-events-modal.span--2">
												<MapPinIcon size={14} data-flx="guild.guild-events-modal.map-pin-icon" /> {event.location}
											</span>
										) : (
											<span data-flx="guild.guild-events-modal.span--3">
												<SpeakerHighIcon size={14} data-flx="guild.guild-events-modal.speaker-high-icon" />{' '}
												{channelName}
											</span>
										)}
									</div>
									{event.description && (
										<p className={styles.description} data-flx="guild.guild-events-modal.description">
											{event.description}
										</p>
									)}
									<div className={styles.actions} data-flx="guild.guild-events-modal.actions">
										<Button
											variant={event.user_interested ? 'primary' : 'secondary'}
											compact
											type="button"
											aria-pressed={event.user_interested}
											leftIcon={
												event.user_interested ? (
													<CheckIcon size={16} weight="bold" data-flx="guild.guild-events-modal.check-icon" />
												) : undefined
											}
											onClick={() => void toggleInterest(event)}
											data-flx="guild.guild-events-modal.button"
										>
											{i18n._(INTERESTED_DESCRIPTOR)}
										</Button>
										{isLive && event.channel_id !== null && (
											<Button
												variant="secondary"
												compact
												type="button"
												onClick={() => openChannel(event.channel_id as string)}
												data-flx="guild.guild-events-modal.button.open-channel"
											>
												<Trans>Join channel</Trans>
											</Button>
										)}
										<span className={styles.count} data-flx="guild.guild-events-modal.count">
											{i18n._(INTERESTED_COUNT_DESCRIPTOR, {count: event.interested_count})}
										</span>
									</div>
								</div>
							);
						})}
					</div>
				)}
			</Modal.Content>
		</Modal.Root>
	);
});

export function openGuildEventsModal(guildId: string): void {
	ModalCommands.push(
		modal(() => (
			<GuildEventsModal
				guildId={guildId}
				data-flx="guild.guild-events-modal.open-guild-events-modal.guild-events-modal"
			/>
		)),
	);
}
